import { useEffect, useRef, useState } from "react";
import type { DeskSession } from "../hooks/useDeskSession";
import { nextOpId } from "../lib/edit-helpers";
import {
  defaultStickerPlacement,
  playheadStickerPlacement,
  clampStickerFineTuneInterval,
} from "../lib/creative-defaults.js";
import {
  APPEAR_FOR_SECONDS,
  DEFAULT_APPEAR_FOR_SECONDS,
  durationTicksFor,
  nextStickerInstanceId,
} from "../lib/sticker-edit.js";
import { coachLineFor } from "./tools.js";
import { ToolCartButton } from "./ToolCartButton.js";
import { StickerGlyph } from "./StickerGlyph.js";
import { useStudioStore } from "./StudioStoreContext.js";
import { stickerColor } from "../lib/sticker-layout.js";
import { stickerArtSrc } from "../lib/sticker-art.js";
import { landingConfigForPack } from "../../content/landing/index.js";
import { stickerComboForPlacement } from "../../game/emotion-model.js";
import { track } from "../../game/analytics.js";

/** "0.1s" / "0.5s" / "1.0s" — labels for the appear-for segmented control. */
function appearForLabel(seconds: number): string {
  return `${seconds.toFixed(1)}s`;
}

/** Nearest appear-for preset for an applied sticker's current window duration. */
function nearestAppearForSeconds(durationTicksSpan: number, tickRate: number): number {
  const seconds = durationTicksSpan / tickRate;
  let best: number = APPEAR_FOR_SECONDS[0];
  let bestDiff = Math.abs(seconds - best);
  for (const candidate of APPEAR_FOR_SECONDS) {
    const diff = Math.abs(seconds - candidate);
    if (diff < bestDiff) {
      best = candidate;
      bestDiff = diff;
    }
  }
  return best;
}

export function DecorateTool({
  session,
  selectedStickerId: controlledSelectedId,
  onSelectSticker,
  playheadSourceTick,
  onSnapPlayheadToSourceTick,
}: {
  session: DeskSession;
  /** Controlled selected sticker instance id (EditorScreen lifts it so the
   *  on-video sticker with this id is the directly-editable one). Falls back
   *  to local state when rendered standalone (tests). */
  selectedStickerId?: string | null;
  onSelectSticker?: (id: string | null) => void;
  /** Current editor playhead, in SOURCE ticks (drop = scrub then tap: a new
   *  sticker's window starts here). Optional so standalone tests still render. */
  playheadSourceTick?: number;
  /** Move the editor playhead to a SOURCE tick (used to snap to a selected
   *  sticker's start so the user sees it). Optional so standalone tests still render. */
  onSnapPlayheadToSourceTick?: (srcTick: number) => void;
}) {
  const { pack, resolved, applyOp } = session;
  const store = useStudioStore();
  const allStickers = pack.stickerOptions ?? [];

  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  // Controlled whenever a change handler is supplied (EditorScreen); otherwise
  // this component owns selection locally (standalone/test rendering). `null`
  // is a valid controlled value (nothing selected), so presence of the
  // handler — not the value — decides which state is authoritative.
  const isControlledSelection = onSelectSticker !== undefined;
  const selectedStickerId = isControlledSelection
    ? (controlledSelectedId ?? null)
    : internalSelectedId;
  const setSelectedStickerId = (id: string | null) => {
    if (!isControlledSelection) setInternalSelectedId(id);
    onSelectSticker?.(id);
  };
  const [appearFor, setAppearFor] = useState<number>(DEFAULT_APPEAR_FOR_SECONDS);

  const activeSticker = resolved.stickers.find((s) => s.id === selectedStickerId);

  // Owned-only options (keep the applied one visible for an unowned loaded graph).
  const stickers = allStickers.filter(
    (s) => store.isOptionOwned("sticker", s.id) || activeSticker?.stickerId === s.id,
  );

  // Placed stickers as an EVENT LIST (like the SFX / effect placed lists): every
  // live instance in timeline order, each selectable (snaps the playhead to its
  // moment) with an inline delete. A cleared instance keeps a null `stickerId`
  // in `resolved.stickers`, so filter those out.
  const stickerLabelFor = (stickerId: string) =>
    allStickers.find((o) => o.id === stickerId)?.label ?? stickerId;
  const placedStickers = resolved.stickers
    .filter((s) => s.stickerId != null)
    .slice()
    .sort((a, b) => a.startTick - b.startTick);

  // Remove one placed instance (mirrors EditorScreen.deleteSticker: a last-wins
  // clear op with a null stickerId; deterministic op id, never Math.random).
  const deleteSticker = (id: string) => {
    const cur = resolved.stickers.find((s) => s.id === id);
    if (!cur?.stickerId) return;
    applyOp({
      kind: "sticker",
      opId: nextOpId("sticker-clear"),
      id,
      stickerId: null,
      anchorXMilli: cur.anchorXMilli,
      anchorYMilli: cur.anchorYMilli,
      scaleMilli: cur.scaleMilli,
      rotationMilli: cur.rotationMilli,
      startTick: 0,
      endTick: 1,
    });
    if (selectedStickerId === id) setSelectedStickerId(null);
  };

  useEffect(() => {
    if (!activeSticker) {
      setAppearFor(DEFAULT_APPEAR_FOR_SECONDS);
      return;
    }
    setAppearFor(
      nearestAppearForSeconds(activeSticker.endTick - activeSticker.startTick, pack.tickRate),
    );
  }, [activeSticker?.stickerId, activeSticker?.startTick, activeSticker?.endTick, pack]);

  // Playhead snap on SELECT (not on every field edit): when the selected
  // sticker instance (id + which sticker is applied there) changes to an
  // applied one, move the playhead to its startTick so the user sees it.
  // Guarded by an identity key so re-committing the SAME sticker (resize,
  // appear-for, move, rotate) never yanks the playhead around.
  const snapKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = activeSticker?.stickerId
      ? `${selectedStickerId}:${activeSticker.stickerId}`
      : null;
    if (!key) {
      snapKeyRef.current = null;
      return;
    }
    if (key !== snapKeyRef.current) {
      snapKeyRef.current = key;
      onSnapPlayheadToSourceTick?.(activeSticker!.startTick);
    }
    // `activeSticker` already carries `stickerId` — listing it separately was redundant.
  }, [selectedStickerId, activeSticker, onSnapPlayheadToSourceTick]);

  // Drop = scrub then tap: ADDS A NEW sticker instance (unlimited — never
  // reuses another instance's id). Its window starts at the current playhead
  // (source ticks) and lasts the current "appear for" duration. The fresh id
  // is deterministic (see `nextStickerInstanceId`) — NOT Math.random/Date.now
  // — so it reproduces identically on replay/undo-redo. The new instance
  // becomes selected so its on-video handles + tool panel appear immediately.
  const applySticker = (stickerId: string) => {
    const sticker = stickers.find((s) => s.id === stickerId);
    if (!sticker) return;
    const place = playheadStickerPlacement(
      pack,
      resolved,
      sticker,
      playheadSourceTick ?? 0,
      appearFor,
    );
    if (!place) return;
    const id = nextStickerInstanceId(session.graph);
    applyOp({
      kind: "sticker",
      opId: nextOpId("sticker"),
      id,
      stickerId,
      anchorXMilli: place.anchorXMilli,
      anchorYMilli: place.anchorYMilli,
      scaleMilli: place.scaleMilli,
      rotationMilli: place.rotationMilli,
      startTick: place.startTick,
      endTick: place.endTick,
    });
    setSelectedStickerId(id);
    // Anonymous analytics: onZone reuses the SAME combo resolver the landing
    // engine/impact chips/sim use, so "landed on a combo zone" agrees everywhere.
    // Cheap (data lookup over this level's authored zones/combos) — no extra
    // state. Wrapped in try/catch (fail-safe, matches the rest of this diff) —
    // an unexpected error here must never block the sticker-drop path itself,
    // so it just falls back to reporting a cosmetic (non-zone) placement.
    let onZone = false;
    try {
      const levers = landingConfigForPack(session.packId).levers;
      onZone = Boolean(
        stickerComboForPlacement(
          stickerId,
          place.anchorXMilli,
          place.anchorYMilli,
          place.startTick,
          place.endTick,
          levers.stickerZones,
          levers.stickerCombos,
        ),
      );
    } catch {
      /* onZone stays false — analytics must never break the sticker drop */
    }
    track({ name: "sticker_placed", props: { stickerId, onZone } });
  };

  // "Appear for" segmented control: re-commits the selected sticker's op with
  // a new endTick (startTick / anchor / scale / rotation untouched). Replaces
  // the old manual start/end fine-tune inputs, and reuses the SAME clamp that
  // path (and the drop path) used: `clampStickerFineTuneInterval` clamps to
  // source end AND — for beat-restricted stickers like sticker-arrow/-frame —
  // only ever returns an interval fully covered by `allowedBeatIds`, or null.
  // We never commit an op the core validator would reject: if no valid window
  // exists for the requested duration, the current window is left unchanged
  // (and the appear-for selection isn't switched to the preset that failed).
  const applyAppearFor = (seconds: number) => {
    if (!activeSticker?.stickerId || !selectedStickerId) {
      setAppearFor(seconds);
      return;
    }
    const stickerOption = stickers.find((s) => s.id === activeSticker.stickerId);
    if (!stickerOption) return;
    const desiredEndTick = activeSticker.startTick + durationTicksFor(seconds, pack.tickRate);
    const clamped = clampStickerFineTuneInterval(
      pack,
      stickerOption,
      activeSticker.startTick,
      desiredEndTick,
    );
    if (!clamped) return;
    setAppearFor(seconds);
    applyOp({
      kind: "sticker",
      opId: nextOpId("sticker"),
      id: selectedStickerId,
      stickerId: activeSticker.stickerId,
      anchorXMilli: activeSticker.anchorXMilli,
      anchorYMilli: activeSticker.anchorYMilli,
      scaleMilli: activeSticker.scaleMilli,
      rotationMilli: activeSticker.rotationMilli,
      startTick: clamped.startTick,
      endTick: clamped.endTick,
    });
  };

  return (
    <div className="tool-panel kid-tool" data-testid="decorate-tool">
      <ToolCartButton tool="decorate" />
      <h3>Stickers</h3>
      <p className="coach-line" data-testid="coach-line">
        {coachLineFor("decorate")}
      </p>

      <div data-testid="decorate-stickers-panel">
        {/* Appear-for at the TOP, one compact line: scrub the video, tap a
            sticker — it drops at that moment and shows for the selected
            duration. The highlighted button IS the current selection. With
            nothing selected this sets the next drop's duration; with a sticker
            selected `applyAppearFor` re-commits that instance's window. */}
        <div
          className="sticker-appear-for"
          data-testid="appear-for"
          role="group"
          aria-label="Appear for"
        >
          <span className="sticker-appear-for-label">Appears for</span>
          {APPEAR_FOR_SECONDS.map((seconds) => {
            const pressed = appearFor === seconds;
            return (
              <button
                key={seconds}
                type="button"
                className={`appear-for-btn${pressed ? " active" : ""}`}
                data-testid={`appear-for-${seconds}`}
                aria-pressed={pressed}
                onClick={() => applyAppearFor(seconds)}
              >
                {appearForLabel(seconds)}
              </button>
            );
          })}
        </div>
        <div className="sticker-grid" data-testid="sticker-cards">
          {stickers.map((s) => {
            const pressed = activeSticker?.stickerId === s.id;
            const place = defaultStickerPlacement(pack, resolved, s);
            return (
              <button
                key={s.id}
                type="button"
                className={`sticker-tile sticker-card${pressed ? " selected" : ""}`}
                data-testid={`sticker-card-${s.id}`}
                aria-pressed={pressed}
                aria-label={s.label}
                title={!place ? "Not available on this story timing" : s.label}
                disabled={!place}
                onClick={() => applySticker(s.id)}
              >
                <StickerGlyph
                  src={stickerArtSrc(s.id)}
                  shape={s.render.kind === "procedural" ? s.render.shape : "badge"}
                  text={s.render.kind === "procedural" ? s.render.text : undefined}
                  color={stickerColor(s.render.kind === "procedural" ? s.render.colorTag : undefined)}
                  fontSize={16}
                />
              </button>
            );
          })}
        </div>

        {/* Placed stickers — the event list (mirrors the SFX / effect lists). */}
        {placedStickers.length > 0 && (
          <div className="sticker-placed" data-testid="sticker-placed">
            {placedStickers.map((op) => {
              const opt = allStickers.find((o) => o.id === op.stickerId);
              const selected = op.id === selectedStickerId;
              const startSec = op.startTick / pack.tickRate;
              return (
                <div
                  key={op.id}
                  className={`sticker-placed-row${selected ? " selected" : ""}`}
                  data-testid={`sticker-instance-${op.id}`}
                >
                  <button
                    type="button"
                    className="sticker-placed-select"
                    data-testid={`sticker-select-${op.id}`}
                    aria-pressed={selected}
                    aria-label={`Select ${stickerLabelFor(op.stickerId!)} at ${startSec.toFixed(1)} seconds`}
                    onClick={() => setSelectedStickerId(op.id)}
                  >
                    <StickerGlyph
                      src={stickerArtSrc(op.stickerId!)}
                      shape={opt?.render.kind === "procedural" ? opt.render.shape : "badge"}
                      text={opt?.render.kind === "procedural" ? opt.render.text : undefined}
                      color={stickerColor(
                        opt?.render.kind === "procedural" ? opt.render.colorTag : undefined,
                      )}
                      fontSize={13}
                    />
                    <span className="sticker-placed-label">{stickerLabelFor(op.stickerId!)}</span>
                    <span className="sticker-placed-time">{startSec.toFixed(1)}s</span>
                  </button>
                  <button
                    type="button"
                    className="sticker-placed-delete"
                    data-testid={`sticker-delete-${op.id}`}
                    aria-label={`Remove ${stickerLabelFor(op.stickerId!)}`}
                    onClick={() => deleteSticker(op.id)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
